// Umsatzverteilung auf Branchen — seit 30.09.2026 über die BRANCHENGRUPPEN des
// Zwei-Ebenen-Katalogs statt über vier feste Zeilen (Automotive, Schifffahrt,
// Luftfahrt, Bahn).
//
// Gespeichert wird im `otherInfo`-Blob eines Kennzahlen-Jahres unter
// `revBranchen: [{ branchengruppeId, active, amount, reason }]`. Kennzahlen-Reiter
// UND Erfassen-Wizard lesen und schreiben ausschließlich über dieses Modul — vorher
// schrieb der Wizard `revBranchAutomotiveAmt` (nur €-String), während der Reiter
// `revBranchAutomotive.amount` las: zwei Quellen für dieselbe Zahl.
//
// ALTWERTE werden beim Lesen auf die passende Branchengruppe abgebildet und NIE
// gelöscht oder umgeschrieben. Ein Eintrag in `revBranchen` für dieselbe Gruppe
// gewinnt vollständig — auch ein geleerter: er bleibt als leerer Eintrag stehen,
// solange ein Altwert darunter liegt, sonst tauchte der Altwert nach dem Leeren
// wieder auf.
//
// Bewusst REACT-FREI (reine Funktionen, node-testbar).

import type { Decoded, FieldRowDef, YesNoNumText } from "./kpiTypes";

/** Ein Eintrag der Umsatzverteilung — Wert je Branchengruppe und Jahr. */
export type RevBrancheEintrag = YesNoNumText & { branchengruppeId: number };

/** Was aus dem Katalog gebraucht wird (kompatibel zu `BrancheGruppe`). */
export type BrancheKatalogEintrag = {
  id: number;
  ebene1?: string;
  ebene2: string;
  active?: boolean;
};

/**
 * Die vier alten Zeilen und ihre Branchengruppe. Die Namen stehen so in
 * `apps/api/src/data/branchengruppen.ts` (Ebene 2 ist über den Katalog eindeutig).
 * Abgebildet wird über den NAMEN, weil die Katalog-Ids Datenbank-Ids sind und
 * je Umgebung anders ausfallen können.
 */
export const ALT_BRANCHEN: readonly { key: string; gruppe: string }[] = [
  { key: "revBranchAutomotive", gruppe: "Fahrzeug- & Verkehrsmittelbau" },
  { key: "revBranchShipping", gruppe: "Schifffahrt & maritime Dienste" },
  { key: "revBranchAviation", gruppe: "Luftfahrt" },
  { key: "revBranchRail", gruppe: "Bahn- & Seilbahnverkehr" },
];

const leer = (v: unknown): boolean => v === undefined || v === null || v === "";

/** Trägt der Wert irgendetwas (Ja/Nein, Betrag oder Begründung)? */
export const istBrancheWertLeer = (v: YesNoNumText | undefined | null): boolean =>
  !v || (leer(v.active) && leer(v.amount) && leer(v.reason));

const alsText = (v: unknown): string | undefined => {
  if (leer(v)) return undefined;
  return typeof v === "string" ? v : typeof v === "number" ? String(v) : undefined;
};

/** Nur die Wert-Felder, ohne leere Keys — so bleibt der Blob kompakt. */
const wertFelder = (v: YesNoNumText): YesNoNumText => {
  const out: YesNoNumText = {};
  if (!leer(v.active)) out.active = v.active;
  if (!leer(v.amount)) out.amount = v.amount;
  if (!leer(v.reason)) out.reason = v.reason;
  return out;
};

/**
 * Altwerte eines Jahres, abgebildet auf Branchengruppen.
 *
 * `.amount` des Objekts (Kennzahlen-Reiter) gewinnt vor `*Amt` (alter Wizard).
 * Ein Betrag aus `*Amt` allein heißt „ja" — der Wizard kannte kein Ja/Nein, und
 * ein erfasster Umsatz in der Branche ist eine Bejahung.
 */
export function altWerteLesen(
  extra: Record<string, unknown>,
  katalog: readonly BrancheKatalogEintrag[],
): RevBrancheEintrag[] {
  const out: RevBrancheEintrag[] = [];
  for (const alt of ALT_BRANCHEN) {
    const gruppe = katalog.find((k) => k.ebene2 === alt.gruppe);
    if (!gruppe) continue;
    const obj = (extra[alt.key] && typeof extra[alt.key] === "object"
      ? extra[alt.key]
      : {}) as YesNoNumText;
    const amount = alsText(obj.amount) ?? alsText(extra[`${alt.key}Amt`]);
    const wert: YesNoNumText = {
      active: obj.active ?? (amount ? true : undefined),
      amount,
      reason: alsText(obj.reason),
    };
    if (istBrancheWertLeer(wert)) continue;
    out.push({ branchengruppeId: gruppe.id, ...wertFelder(wert) });
  }
  return out;
}

/** Die im neuen Format gespeicherten Einträge — defensiv gegen kaputte Blobs. */
const neueEintraege = (extra: Record<string, unknown>): RevBrancheEintrag[] => {
  const roh = extra.revBranchen;
  if (!Array.isArray(roh)) return [];
  return roh.filter(
    (e): e is RevBrancheEintrag =>
      !!e && typeof e === "object" && Number.isFinite((e as RevBrancheEintrag).branchengruppeId),
  );
};

/**
 * Umsatzverteilung eines Jahres: neue Einträge plus Altwerte für Gruppen, die
 * (noch) keinen neuen Eintrag haben. Leere Einträge (reine Altwert-Schatten)
 * fallen heraus.
 */
export function revBranchenLesen(
  extra: Record<string, unknown>,
  katalog: readonly BrancheKatalogEintrag[],
): RevBrancheEintrag[] {
  const neu = neueEintraege(extra);
  const belegt = new Set(neu.map((e) => e.branchengruppeId));
  const alt = altWerteLesen(extra, katalog).filter((e) => !belegt.has(e.branchengruppeId));
  return [...neu, ...alt].filter((e) => !istBrancheWertLeer(e));
}

/** Wert einer einzelnen Branchengruppe (leeres Objekt, wenn nichts erfasst). */
export function revBrancheWert(
  extra: Record<string, unknown>,
  branchengruppeId: number,
  katalog: readonly BrancheKatalogEintrag[],
): YesNoNumText {
  const e = revBranchenLesen(extra, katalog).find((x) => x.branchengruppeId === branchengruppeId);
  if (!e) return {};
  const { branchengruppeId: _id, ...wert } = e;
  return wert;
}

/**
 * Schreibt den Wert EINER Branchengruppe. Liefert eine Kopie, in der nur
 * `revBranchen` ersetzt ist — die Alt-Keys bleiben unangetastet.
 *
 * Ein geleerter Wert bleibt als leerer Eintrag stehen, wenn darunter ein
 * Altwert liegt (sonst käme der beim nächsten Lesen zurück); ohne Altwert
 * verschwindet der Eintrag.
 */
export function revBrancheSchreiben<T extends Record<string, unknown>>(
  extra: T,
  branchengruppeId: number,
  wert: YesNoNumText,
  katalog: readonly BrancheKatalogEintrag[],
): T & { revBranchen: RevBrancheEintrag[] } {
  const liste = neueEintraege(extra).filter((e) => e.branchengruppeId !== branchengruppeId);
  const bereinigt = wertFelder(wert ?? {});
  const hatAltwert = altWerteLesen(extra, katalog).some(
    (e) => e.branchengruppeId === branchengruppeId,
  );
  if (!istBrancheWertLeer(bereinigt) || hatAltwert) {
    liste.push({ branchengruppeId, ...bereinigt });
  }
  return { ...extra, revBranchen: liste };
}

/** Eine Zeile der Umsatzverteilung, wie Reiter und Wizard sie anzeigen. */
export type BrancheZeile = {
  branchengruppeId: number;
  label: string;
  /** Gehört die Gruppe (noch) zu den Branchen der Firma? */
  zugeordnet: boolean;
};

/**
 * Zeilen der Umsatzverteilung: zuerst die Branchengruppen der Firma (in ihrer
 * Reihenfolge), dann Gruppen mit gespeichertem Wert, die nicht (mehr) zur Firma
 * gehören, dann per „+ Branche" hinzugefügte. Jede Gruppe genau einmal.
 *
 * Eine gespeicherte Gruppe, die im Katalog fehlt (gelöscht), bleibt mit
 * Ersatz-Beschriftung sichtbar — ihr Wert ginge sonst unsichtbar verloren.
 */
export function brancheZeilenMergen(args: {
  firmenGruppenIds: readonly number[];
  gespeichertIds: Iterable<number>;
  hinzugefuegtIds?: Iterable<number>;
  katalog: readonly BrancheKatalogEintrag[];
}): BrancheZeile[] {
  const { firmenGruppenIds, gespeichertIds, hinzugefuegtIds = [], katalog } = args;
  const byId = new Map(katalog.map((k) => [k.id, k]));
  const firma = new Set(firmenGruppenIds);
  const out: BrancheZeile[] = [];
  const gesehen = new Set<number>();
  const add = (id: number) => {
    if (gesehen.has(id) || !Number.isFinite(id) || id <= 0) return;
    gesehen.add(id);
    out.push({
      branchengruppeId: id,
      label: byId.get(id)?.ebene2 ?? `Branchengruppe #${id}`,
      zugeordnet: firma.has(id),
    });
  };
  for (const id of firmenGruppenIds) add(id);
  for (const id of gespeichertIds) add(id);
  for (const id of hinzugefuegtIds) add(id);
  return out;
}

/** Präfix der Zeilen-Ids im Kennzahlen-Reiter (`revBranche_<branchengruppeId>`). */
export const REV_BRANCHE_ROW_PREFIX = "revBranche_";
/** Gruppenkopf, unter dem die Branchen-Zeilen im Reiter hängen. */
export const REV_BRANCHEN_HEADER_ID = "umsatz_branchen_header";

/**
 * Feldzeilen des Kennzahlen-Reiters für die Umsatzverteilung — eine je
 * Branchengruppe. Lesen/Schreiben gehen über `revBrancheWert`/`revBrancheSchreiben`.
 */
export function brancheFeldZeilen(
  zeilen: readonly BrancheZeile[],
  katalog: readonly BrancheKatalogEintrag[],
): FieldRowDef[] {
  return zeilen.map((z) => ({
    kind: "field" as const,
    sectionId: "umsatz",
    id: `${REV_BRANCHE_ROW_PREFIX}${z.branchengruppeId}`,
    groupId: REV_BRANCHEN_HEADER_ID,
    label: z.zugeordnet ? z.label : `${z.label} (nicht in der Akte)`,
    type: "yesNoNumText" as const,
    indent: 1 as const,
    get: (y: Decoded) => revBrancheWert(y, z.branchengruppeId, katalog),
    set: (y: Decoded, v: YesNoNumText) => revBrancheSchreiben(y, z.branchengruppeId, v ?? {}, katalog),
  }));
}

// ---- Erfassen-Wizard ------------------------------------------------------
//
// Der Wizard erfasst je Branchengruppe nur den Betrag (€) — in seiner flachen
// Antwort-Map unter demselben Schlüssel wie die Zeilen-Id des Reiters
// (`revBranche_<id>`). Gelesen und geschrieben wird über dieselben Funktionen
// wie im Reiter; es gibt nur noch EIN Format.

/** Antwort-Schlüssel einer Branchengruppe im Wizard (ohne Jahres-Suffix). */
export const wizardBrancheKey = (branchengruppeId: number): string =>
  `${REV_BRANCHE_ROW_PREFIX}${branchengruppeId}`;

/** Beträge je Branchengruppe aus einem `otherInfo`-Blob (inkl. Altwerte). */
export function wizardBetraegeLesen(
  extra: Record<string, unknown>,
  katalog: readonly BrancheKatalogEintrag[],
): Map<number, string> {
  const out = new Map<number, string>();
  for (const e of revBranchenLesen(extra, katalog)) {
    if (!leer(e.amount)) out.set(e.branchengruppeId, String(e.amount));
  }
  return out;
}

/**
 * Überträgt die Wizard-Beträge eines Jahres in den `otherInfo`-Blob.
 *
 * Nur GEÄNDERTE Beträge werden geschrieben: ein unangetasteter Altwert bleibt
 * damit ein Altwert, statt beim bloßen Durchklicken in `revBranchen` kopiert zu
 * werden. Ja/Nein und Begründung aus dem Reiter bleiben erhalten; ein neuer
 * Betrag heißt „ja", ein geleerter lässt das Ja/Nein stehen.
 */
export function wizardBetraegeSchreiben<T extends Record<string, unknown>>(
  extra: T,
  betraege: ReadonlyMap<number, string>,
  katalog: readonly BrancheKatalogEintrag[],
): T & { revBranchen?: RevBrancheEintrag[] } {
  let out: T & { revBranchen?: RevBrancheEintrag[] } = extra;
  for (const [id, rohBetrag] of betraege) {
    const betrag = (rohBetrag ?? "").trim();
    const alt = revBrancheWert(out, id, katalog);
    if ((alt.amount ?? "") === betrag) continue;
    out = revBrancheSchreiben(out, id, {
      ...alt,
      amount: betrag,
      active: betrag ? true : alt.active,
    }, katalog);
  }
  return out;
}
